import { createSlice } from "@reduxjs/toolkit";
import { getActiveInvites, deactivateInvite, sendEmail, addGuest, deactivateInvitationByAvatarId } from "./inviteThunk";

const initialState = {
    invitationList: [],
    loading: false,
    error: null,
    activeTab: "inviteUser",
    invitedUser: "",
    deleteInvitation: null,
    deactivateInvitationByAvatarId: null,
    confirmDialogOpen: false,
    // Form state
    email: "",
    expirationTime: "",
    deleteInvitationId: null
};

const inviteSlice = createSlice({
  name: "inviteModal",
  initialState,
  reducers: {
    setInvitationList: (state, action) => {
        if (!Array.isArray(action.payload)) {
            console.error("Payload is not an array:", action.payload);
            return;
        }
        state.invitationList = action.payload;
    },
    setLoading: (state, action) => {
        state.loading = action.payload;
    },
    setError: (state, action) => {
        state.error = action.payload;
    },
    setActiveTab: (state, action) => {
        state.activeTab = action.payload;        
    },
    setInvitedUser: (state, action) => {
        state.invitedUser = action.payload;
    },
    setDeleteInvitation: (state, action) => {
        state.deleteInvitation = action.payload;
    },
    setConfirmDialogOpen: (state, action) => {
        state.confirmDialogOpen = action.payload;
    },
    clearError: (state) => {
        state.error = null;
    },
    // Form state actions
    setEmail: (state, action) => {
        state.email = action.payload;
    },
    setExpirationTime: (state, action) => {
        state.expirationTime = action.payload;
    },
    setDeleteInvitationId: (state, action) => {
        state.deleteInvitationId = action.payload;
    },
    clearForm: (state) => {
        state.email = "";
        state.expirationTime = "";
    }
  },
  extraReducers: (builder) => {
    builder
      // getActiveInvites cases
      .addCase(getActiveInvites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActiveInvites.fulfilled, (state, action) => {
        const invites = action.payload;
        state.invitationList = Array.isArray(invites)
          ? invites.filter(invite => !invite.is_expired && invite.user_map?.is_guest)
          : [];
        state.loading = false;
        state.error = null;
      })
      .addCase(getActiveInvites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to fetch active invites";
        console.error("Failed to fetch active invites:", action.error);
      })
      
      // deactivateInvite cases
      .addCase(deactivateInvite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateInvite.fulfilled, (state, action) => {
        state.loading = false;
        // Get the ID from the request body or use deleteInvitationId
        const deactivatedId = action.meta.arg?.params?.[0].id;
        if (deactivatedId) {
          state.invitationList = state.invitationList.filter(
            invite => String(invite.id) !== String(deactivatedId)
          );
        }
        state.deleteInvitation = null;
        state.deleteInvitationId = null; // Clear after successful deletion
        state.error = null;
      })
      .addCase(deactivateInvite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to deactivate invite";
        console.error("Failed to deactivate invite:", action.error);
      })
      .addCase(deactivateInvitationByAvatarId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateInvitationByAvatarId.fulfilled, (state, action) => {
        state.loading = false;
        const deactivatedId = action.meta.arg?.params?.[0].id;
        if (deactivatedId) {
          state.invitationList = state.invitationList.filter(
            invite => String(invite.user_map?.id) !== String(deactivatedId)
          );
        }
        state.deactivateInvitationByAvatarId = null;
        state.error = null;
      })
      .addCase(deactivateInvitationByAvatarId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to deactivate invitation by avatar ID";
        console.error("Failed to deactivate invitation by avatar ID:", action.error);
      })
      
      // sendEmail cases
      .addCase(sendEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.invitedUser = action.payload;
        state.error = null;
      })
      .addCase(sendEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to send email";
        console.error("Failed to send email:", action.error);
      })
      
      // addGuest cases (if this thunk exists)
      .addCase(addGuest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addGuest.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Clear form on successful submission
        state.email = "";
        state.expirationTime = "";
        // Optionally add the new guest to the invitation list
        if (action.payload && state.activeTab === "invitationList") {
          state.invitationList.push(action.payload);
        }
      })
      .addCase(addGuest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to add guest";
        console.error("Failed to add guest:", action.error);
      });
  }
});

export const {
  setInvitationList,
  setLoading,
  setError,
  setActiveTab,
  setInvitedUser,
  setDeleteInvitation,
  setConfirmDialogOpen,
  clearError,
  setEmail,
  setExpirationTime,
  setDeleteInvitationId,
  clearForm
} = inviteSlice.actions;

export default inviteSlice.reducer;